import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from "@tanstack/react-query";
import { authService } from '@/services/authService';
import type { User } from '@/services/apiClient';

// Global flag to prevent multiple auth initializations
let globalAuthInitialized = false;

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const initRef = useRef(false);

  // Initialize auth on mount
  useEffect(() => {
    // Prevent multiple initializations
    if (initRef.current || globalAuthInitialized) {
      return;
    }
    
    initRef.current = true;
    globalAuthInitialized = true;
    
    const initAuth = () => {
      try {
        console.log('🔐 Initializing auth (one-time)...');
        
        // Get stored user
        const storedUser = authService.getStoredUser();
        
        if (storedUser) {
          console.log('🔐 Found stored user:', storedUser.name);
          setUser(storedUser);
        } else {
          console.log('🔐 No stored user - user needs to login');
          // Don't create mock user when using real API
          // Only set user to null and let login page handle authentication
          setUser(null);
        }
      } catch (error) {
        console.error('🔐 Auth initialization failed:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    // Initialize auth
    initAuth();
    
    // Cleanup
    return () => {
      // Don't reset global flag on cleanup to prevent re-initialization
    };
  }, []); // Empty deps - run only once

  // Reload user from localStorage when it changes (for login updates)
  useEffect(() => {
    const checkUserUpdate = () => {
      const storedUser = authService.getStoredUser();
      if (storedUser) {
        // Only update if user is different
        if (!user || user.id !== storedUser.id || JSON.stringify(user) !== JSON.stringify(storedUser)) {
          console.log('🔐 User updated in localStorage, reloading:', storedUser.name);
          setUser(storedUser);
        }
      } else if (user) {
        // User was removed from localStorage
        console.log('🔐 User removed from localStorage');
        setUser(null);
      }
    };

    // Check immediately
    checkUserUpdate();

    // Listen for storage events (cross-tab updates)
    window.addEventListener('storage', checkUserUpdate);

    // Poll for localStorage changes (for same-tab updates)
    // Check every 1 second - this ensures user updates after login
    const interval = setInterval(checkUserUpdate, 1000);

    return () => {
      window.removeEventListener('storage', checkUserUpdate);
      clearInterval(interval);
    };
  }, [user]); // Re-run when user changes

  const login = async (phone: string, password: string) => {
    setIsLoading(true);
    
    // Force clear loading after 10 seconds no matter what (increased timeout)
    const timeoutId = setTimeout(() => {
      console.warn('🔐 Login timeout - clearing loading state');
      setIsLoading(false);
    }, 10000);
    
    try {
      console.log('🔐 Login attempt:', { phone });
      
      // Mark that a real auth attempt was made
      localStorage.setItem('attempted_real_auth', 'true');
      
      const response = await authService.login({ phone, password });
      
      console.log('🔐 Full login response:', JSON.stringify(response, null, 2));
      
      // Extract user and tokens from response structure: { success: true, data: { user: {...}, token: { access: "...", refresh: "..." } } }
      let userData: User | null = null;
      
      // Check response structure - handle both direct and nested
      if (response.success && response.data) {
        console.log('🔐 Response has success and data:', {
          hasUser: !!response.data.user,
          hasToken: !!response.data.token,
          dataKeys: Object.keys(response.data)
        });
        
        // New structure: response.data contains { user, token: { access, refresh } }
        if (response.data.user) {
          userData = response.data.user;
          console.log('🔐 User extracted from response:', userData);
        }
        
        // Extract tokens (apiClient should already store them, but we'll ensure they're available)
        const tokenData = response.data.token;
        if (tokenData) {
          console.log('🔐 Token data found:', typeof tokenData === 'object' ? 'object' : 'string');
          // Handle both object format { access, refresh } and string format
          const accessToken = typeof tokenData === 'object' ? tokenData.access : tokenData;
          const refreshToken = typeof tokenData === 'object' ? tokenData.refresh : null;
          
          if (accessToken) {
            localStorage.setItem('authToken', accessToken);
            console.log('🔐 Access token stored');
          }
          if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
            console.log('🔐 Refresh token stored');
          }
        }
      } else {
        console.warn('🔐 Response structure unexpected:', {
          success: response.success,
          hasData: !!response.data,
          message: response.message
        });
      }
      
      // If no user found in response, check localStorage (apiClient might have saved it)
      if (!userData) {
        const storedUser = authService.getStoredUser();
        if (storedUser) {
          userData = storedUser;
          console.log('🔐 Using user from localStorage:', userData);
        }
      }
      
      // Save user to state and localStorage
      if (userData && (userData.id || userData.name || userData.phone)) {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        console.log('🔐 Login successful - User saved:', {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          roleId: userData.roleId
        });
        clearTimeout(timeoutId);
        setIsLoading(false);
        return { success: true, user: userData };
      } else {
        const errorMsg = response.message || 'Login failed: No user data received';
        console.error('🔐 Login failed - no user data:', {
          response,
          userData,
          storedUser: authService.getStoredUser()
        });
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      console.error('🔐 Login error caught:', error);
      console.error('🔐 Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      clearTimeout(timeoutId);
      setIsLoading(false);
      throw error;
    }
  };

  const logout = () => {
    console.log('🔐 Logging out...');
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('attempted_real_auth');
    queryClient.clear();
  };

  const updateProfile = async (profileData: Partial<User>) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      const updatedUser = { ...user, ...profileData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('🔐 Profile update failed:', error);
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      // Implementation would call API
      console.log('🔐 Password change requested');
      return { success: true };
    } catch (error) {
      console.error('🔐 Password change failed:', error);
      throw error;
    }
  };

  const refreshToken = async () => {
    try {
      // Implementation would refresh token
      console.log('🔐 Token refresh requested');
      return { success: true };
    } catch (error) {
      console.error('🔐 Token refresh failed:', error);
      throw error;
    }
  };

  // Helper functions
  const hasRole = (roleId: number): boolean => {
    return user?.roleId === roleId;
  };

  const isAdmin = hasRole(2);
  const isStaff = hasRole(3);
  const hasAdminAccess = isAdmin || isStaff;

  return {
    user,
    isLoading,
    login,
    logout,
    updateProfile,
    changePassword,
    refreshToken,
    hasRole,
    isAdmin,
    isStaff,
    hasAdminAccess,
    isAuthenticated: !!user,
    error: null
  };
}